/**
 * useFluxSupply — V2.K.2
 *
 * Fetches FLUX supply data from CoinGecko :
 *  - circulating_supply : tokens freely tradable
 *  - total_supply       : all tokens currently in existence
 *  - max_supply         : hard cap (PoUW v2 raised it from 440 M to
 *                         560 M when it activated at blockheight
 *                         2 020 000 on 25 October 2025)
 *
 * Note : Flux is a UTXO chain (Zcash fork) — unlike Cronos there is
 * NO big locked treasury escrow. Total supply ≈ circulating supply.
 * The interesting "locked" figure here is actually the amount
 * collateralized in FluxNodes (~43 % of circulating per RunOnFlux
 * docs), which CoinGecko doesn't expose — we surface it qualitatively
 * in the UI instead of computing a misleading delta.
 *
 * CoinGecko coin id : `zelcash` (legacy name kept after rebrand).
 *
 * Refresh : every 5 minutes.
 */

import { useQuery } from '@tanstack/react-query';

export interface FluxSupplyData {
  circulating: number;
  total: number;
  max: number;
  /** % of max supply already minted. */
  mintedPct: number;
  /** % of total supply currently in circulation. */
  floatPct: number;
  /** Remaining to mint before reaching the cap. */
  remainingToMint: number;
  /** ISO timestamp of CoinGecko's last refresh. */
  lastUpdated: string | null;
}

// PoUW v2 (active 25 Oct 2025, blockheight 2 020 000) raised the cap
// from 440 M to 560 M. CoinGecko sometimes still reports null —
// we fall back on the post-fork value.
const FLUX_MAX_SUPPLY_FALLBACK = 560_000_000;

async function fetchFluxSupply(): Promise<FluxSupplyData> {
  const response = await fetch(
    'https://api.coingecko.com/api/v3/coins/zelcash?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false',
  );
  if (!response.ok) {
    throw new Error(`CoinGecko returned ${response.status}`);
  }
  const json = await response.json();
  const md = json.market_data || {};
  const circulating = Number(md.circulating_supply) || 0;
  const total = Number(md.total_supply) || circulating;
  // CoinGecko sometimes reports max_supply as null for Flux — fall
  // back to the whitepaper figure rather than show 0 / NaN.
  const max =
    Number(md.max_supply) > 0 ? Number(md.max_supply) : FLUX_MAX_SUPPLY_FALLBACK;

  return {
    circulating,
    total,
    max,
    mintedPct: max > 0 ? (total / max) * 100 : 0,
    floatPct: total > 0 ? (circulating / total) * 100 : 0,
    remainingToMint: Math.max(0, max - total),
    lastUpdated: json.last_updated || null,
  };
}

export function useFluxSupply() {
  return useQuery({
    queryKey: ['flux', 'supply'],
    queryFn: fetchFluxSupply,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
  });
}
